import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Edit, Plus, RefreshCw, Search, Trash2, FileText, CheckCircle, XCircle, AlertTriangle, Download, Check, X } from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';
import { ALL_FILIALES_ID } from '../data/filiales';

const Absences = () => {
  const { user } = useAuth();
  const { currentFiliale } = useFiliale();
  const navigate = useNavigate();

  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [formData, setFormData] = useState({
    personnel_id: '',
    date_debut: '',
    date_fin: '',
    nbre_heures: '',
    type_absence_id: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const [personnelList, setPersonnelList] = useState([]);
  const [typeAbsenceList, setTypeAbsenceList] = useState([]);

  const [showPersonnelModal, setShowPersonnelModal] = useState(false);
  const [personnelSearch, setPersonnelSearch] = useState('');

  const [attenteValidationList, setAttenteValidationList] = useState([]);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [rejetMotif, setRejetMotif] = useState('');

  // Calcul automatique du nombre d'heures
  useEffect(() => {
    if (formData.date_debut && formData.date_fin) {
      const debut = new Date(formData.date_debut);
      const fin = new Date(formData.date_fin);
      
      if (!isNaN(debut.getTime()) && !isNaN(fin.getTime()) && fin >= debut) {
        const diffTime = Math.abs(fin - debut);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 pour inclure le jour de début
        const heures = diffDays * 8; // 8 heures par jour
        setFormData(prev => ({ ...prev, nbre_heures: heures.toString() }));
      }
    }
  }, [formData.date_debut, formData.date_fin]);

  const fetchAbsences = async () => {
    try {
      setLoading(true);
      const params = {};

      if (currentFiliale && currentFiliale.id !== ALL_FILIALES_ID) {
        params.filiale_id = currentFiliale.id;
      }
      if (typeFilter) params.type_absence_id = typeFilter;
      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin) params.date_fin = dateFin;

      const response = await http.get('/api/absences', { params });
      setAbsences(response.data || []);
    } catch (error) {
      console.error('Erreur récupération absences:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPersonnel = async () => {
    try {
      let url = '/api/personnel';
      if (currentFiliale && currentFiliale.id !== ALL_FILIALES_ID) {
        url += `?filiale_id=${currentFiliale.id}`;
      }
      const response = await http.get(url);
      const data = response.data.data || response.data;
      setPersonnelList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur récupération personnel:', error);
      setPersonnelList([]);
    }
  };

  const fetchTypeAbsence = async () => {
    try {
      const response = await http.get('/api/type-absence');
      setTypeAbsenceList(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur récupération types absence:', error);
      setTypeAbsenceList([]);
    }
  };

  const fetchAttenteValidation = async () => {
    try {
      const response = await http.get('/api/absences/attente-validation');
      setAttenteValidationList(response.data || []);
    } catch (error) {
      console.error('Erreur récupération attente validation:', error);
    }
  };

  useEffect(() => {
    fetchAbsences();
    fetchPersonnel();
    fetchTypeAbsence();
    fetchAttenteValidation();
  }, [refreshKey, currentFiliale, typeFilter, dateDebut, dateFin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('FormData à envoyer:', formData);
      if (editData) {
        await http.put(`/api/absences/${editData.idabsence}`, formData);
      } else {
        await http.post('/api/absences', {
          ...formData,
          insertdate: new Date().toISOString().split('T')[0]
        });
      }
      setShowModal(false);
      setEditData(null);
      setFormData({
        personnel_id: '',
        date_debut: '',
        date_fin: '',
        nbre_heures: '',
        type_absence_id: ''
      });
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Erreur soumission:', error);
      console.error('Détails erreur:', error.response?.data);
      alert(`Erreur lors de la soumission: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleEdit = (absence) => {
    setEditData(absence);
    setFormData({
      personnel_id: absence.personnel_id,
      date_debut: absence.date_debut,
      date_fin: absence.date_fin,
      nbre_heures: absence.nbre_heures,
      type_absence_id: absence.type_absence_id
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette absence ?')) return;
    try {
      await http.delete(`/api/absences/${id}`);
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleValider = async (id) => {
    try {
      await http.put(`/api/absences/${id}/valider`);
      setRefreshKey(prev => prev + 1);
      alert('Certificat validé avec succès');
    } catch (error) {
      console.error('Erreur validation:', error);
      alert('Erreur lors de la validation');
    }
  };

  const handleRejeter = async () => {
    if (!rejetMotif.trim()) {
      alert('Veuillez saisir le motif du rejet');
      return;
    }
    try {
      await http.put(`/api/absences/${selectedAbsence.idabsence}/rejeter`, { motif_rejet: rejetMotif });
      setShowValidationModal(false);
      setSelectedAbsence(null);
      setRejetMotif('');
      setRefreshKey(prev => prev + 1);
      alert('Certificat rejeté avec succès');
    } catch (error) {
      console.error('Erreur rejet:', error);
      alert('Erreur lors du rejet');
    }
  };

  const handleDownloadCertificat = async (certificatId, fileName) => {
    try {
      const response = await http.get(`/api/certificats/${certificatId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      alert('Erreur lors du téléchargement');
    }
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'en_attente_justification':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">En attente</span>;
      case 'en_attente_validation':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">À valider</span>;
      case 'justifiee':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Justifiée</span>;
      case 'non_justifiee':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Non justifiée</span>;
      case 'enregistree':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Enregistrée</span>;
      case 'sanction':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Sanction</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{statut}</span>;
    }
  };

  const filteredPersonnel = personnelList.filter(p =>
    p.nom.toLowerCase().includes(personnelSearch.toLowerCase()) ||
    p.prenom.toLowerCase().includes(personnelSearch.toLowerCase())
  );

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Gestion des absences"
        subtitle="Signaler et gérer les absences du personnel"
        actions={[
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: () => setRefreshKey(prev => prev + 1),
            primary: false
          },
          {
            label: 'Signaler une absence',
            icon: Plus,
            onClick: () => setShowModal(true),
            primary: true
          }
        ]}
      />

      <div className="px-6 py-8 space-y-6">
      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">En attente de justification</p>
              <p className="text-2xl font-bold text-slate-900">
                {absences.filter(a => a.statut === 'en_attente_justification').length}
              </p>
            </div>
            <AlertTriangle className="h-8 w-8 text-yellow-500" />
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">En attente de validation</p>
              <p className="text-2xl font-bold text-slate-900">
                {attenteValidationList.length}
              </p>
            </div>
            <FileText className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Justifiées</p>
              <p className="text-2xl font-bold text-slate-900">
                {absences.filter(a => a.statut === 'justifiee').length}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Non justifiées</p>
              <p className="text-2xl font-bold text-slate-900">
                {absences.filter(a => a.statut === 'non_justifiee').length}
              </p>
            </div>
            <XCircle className="h-8 w-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Recherche</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Nom, prénom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Type d'absence</label>
            <select
              value={typeFilter || ''}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none"
            >
              <option value="">Tous</option>
              {typeAbsenceList.map(type => (
                <option key={type.idtype_absence} value={type.idtype_absence}>{type.libelle}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Date début</label>
            <input
              type="date"
              value={dateDebut || ''}
              onChange={(e) => setDateDebut(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Date fin</label>
            <input
              type="date"
              value={dateFin || ''}
              onChange={(e) => setDateFin(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Tableau des absences */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-200">
          <h3 className="text-lg font-medium text-slate-900">Liste des absences</h3>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500">Chargement...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-sky-50/60">
                <tr>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Employé</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Date début</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Date fin</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Heures</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Statut</th>
                  <th className="px-6 py-3.5 text-right text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {absences
                  .filter(a =>
                    a.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    a.prenom?.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((absence) => (
                    <tr key={absence.idabsence} className="hover:bg-sky-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-900">
                          {absence.prenom} {absence.nom}
                        </div>
                        <div className="text-sm text-slate-500">{absence.matricule}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        {formatDate(absence.date_debut)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        {formatDate(absence.date_fin)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        {absence.nbre_heures}h
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        {absence.type_absence_libelle}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {getStatutBadge(absence.statut)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex justify-end gap-2">
                          {absence.certificat_id && (
                            <button
                              onClick={() => handleDownloadCertificat(absence.certificat_id, `certificat-${absence.idabsence}.pdf`)}
                              className="text-green-600 hover:text-green-800"
                              title="Télécharger le certificat"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                          )}
                          {absence.statut === 'en_attente_validation' && (
                            <>
                              <button
                                onClick={() => handleValider(absence.idabsence)}
                                className="text-green-600 hover:text-green-800"
                                title="Valider"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedAbsence(absence);
                                  setShowValidationModal(true);
                                }}
                                className="text-red-600 hover:text-red-800"
                                title="Rejeter"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {absence.statut !== 'justifiee' && absence.statut !== 'non_justifiee' && absence.statut !== 'enregistree' && (
                            <>
                              <button
                                onClick={() => handleEdit(absence)}
                                className="text-blue-600 hover:text-blue-800"
                                title="Modifier"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(absence.idabsence)}
                                className="text-red-600 hover:text-red-800"
                                title="Supprimer"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal création/modification */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-4">
              <h3 className="text-lg font-bold text-slate-900">
                {editData ? 'Modifier une absence' : 'Signaler une absence'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditData(null);
                  setFormData({
                    personnel_id: '',
                    date_debut: '',
                    date_fin: '',
                    nbre_heures: '',
                    type_absence_id: ''
                  });
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Employé
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={(() => {
                        const selected = personnelList.find(p => String(p.idpersonnel) === String(formData.personnel_id));
                        return selected ? `${selected.prenom} ${selected.nom}` : '';
                      })()}
                      className="flex-1 rounded-lg border border-slate-200 px-4 py-2 text-sm bg-slate-50"
                      placeholder="Sélectionner un employé"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPersonnelModal(true)}
                      className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700"
                    >
                      Choisir
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Date début
                    </label>
                    <input
                      type="date"
                      className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-sky-500 focus:outline-none"
                      value={formData.date_debut}
                      onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Date fin
                    </label>
                    <input
                      type="date"
                      className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-sky-500 focus:outline-none"
                      value={formData.date_fin}
                      onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Nombre d'heures
                    </label>
                    <input
                      type="number"
                      className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-sky-500 focus:outline-none"
                      value={formData.nbre_heures}
                      onChange={(e) => setFormData({ ...formData, nbre_heures: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Type d'absence
                    </label>
                    <select
                      className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-sky-500 focus:outline-none"
                      value={formData.type_absence_id}
                      onChange={(e) => setFormData({ ...formData, type_absence_id: e.target.value })}
                      required
                    >
                      <option value="">Sélectionner</option>
                      {typeAbsenceList.filter(type => type.idtype_absence !== 2).map(type => (
                        <option key={type.idtype_absence} value={type.idtype_absence}>{type.libelle}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3 border-t border-slate-200 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditData(null);
                    setFormData({
                      personnel_id: '',
                      date_debut: '',
                      date_fin: '',
                      nbre_heures: '',
                      type_absence_id: ''
                    });
                  }}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700"
                >
                  {editData ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal sélection personnel */}
      {showPersonnelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-4">
              <h3 className="text-lg font-bold text-slate-900">Sélectionner un employé</h3>
              <button
                onClick={() => setShowPersonnelModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Rechercher..."
                value={personnelSearch}
                onChange={(e) => setPersonnelSearch(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-sky-500 focus:outline-none"
              />
            </div>
            <div className="max-h-96 overflow-y-auto">
              {filteredPersonnel.map((personnel) => (
                <div
                  key={personnel.idpersonnel}
                  onClick={() => {
                    setFormData({ ...formData, personnel_id: personnel.idpersonnel });
                    setShowPersonnelModal(false);
                    setPersonnelSearch('');
                  }}
                  className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer rounded-lg"
                >
                  <div className="h-10 w-10 rounded-full bg-sky-100 flex items-center justify-center text-sky-600 font-semibold">
                    {personnel.photo_profil || personnel.photo ? (
                      <img
                        src={personnel.photo_profil || personnel.photo}
                        alt=""
                        className="h-10 w-10 rounded-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <span className={`${personnel.photo_profil || personnel.photo ? 'hidden' : 'flex'}`}>
                      {personnel.prenom?.charAt(0)?.toUpperCase()}{personnel.nom?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-900">{personnel.prenom} {personnel.nom}</div>
                    <div className="text-xs text-slate-500">{personnel.matricule}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal rejet */}
      {showValidationModal && selectedAbsence && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-4">
              <h3 className="text-lg font-bold text-slate-900">Rejeter le certificat</h3>
              <button
                onClick={() => {
                  setShowValidationModal(false);
                  setSelectedAbsence(null);
                  setRejetMotif('');
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Motif du rejet
                </label>
                <textarea
                  value={rejetMotif}
                  onChange={(e) => setRejetMotif(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-sky-500 focus:outline-none"
                  rows="3"
                  required
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowValidationModal(false);
                    setSelectedAbsence(null);
                    setRejetMotif('');
                  }}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                >
                  Annuler
                </button>
                <button
                  onClick={handleRejeter}
                  className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700"
                >
                  Rejeter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Absences;
